import { fetchCollectionTitles } from './klaviyo-rebuy-atc.js';
import { getPromoTags } from './promo-tag-utils.js';

window.BSS = window.BSS || {};
BSS.Components = BSS.Components || {};

/**
 * Defines the `BSS.Components.ProductRecommendations` module which fetches product recommendations
 * from Rebuy and renders them using the product-card web component
 * @module BSS.Components.ProductRecommendations
 * @returns {Object} An object exposing public methods for product recommendations
 */
BSS.Components.ProductRecommendations = (function () {
  // Rebuy API configuration
  const REBUY_API_KEY = '92d7bbc7a53a1ca6ad2ba36f5d00a3efecd8ec16';
  const REBUY_RULESET_ID = '152133';
  const REBUY_BASE_URL = `https://rebuyengine.com/api/v1/custom/id/${REBUY_RULESET_ID}?key=${REBUY_API_KEY}`;

  /**
   * Maps Rebuy product data to the format expected by the product-card component
   * @param {Object} rebuyProduct - Product data from Rebuy API
   * @returns {Promise<Object>} Mapped product data
   */
  async function mapRebuyProductToProduct(rebuyProduct) {
    if (!rebuyProduct) return null;

    // Ensure we have required data
    if (!rebuyProduct.variants || rebuyProduct.variants.length === 0) {
      console.warn('Product missing variants:', rebuyProduct);
      return null;
    }

    // Ensure tags is always an array
    let tags = [];
    if (Array.isArray(rebuyProduct?.tags)) {
      tags = rebuyProduct?.tags;
    } else if (typeof rebuyProduct?.tags === 'string') {
      tags = rebuyProduct?.tags.split(',').map(tag => tag.trim());
    }

    // Fetch collection titles from collection_ids using Shopify Storefront API for Klaviyo integration
    let collections = [];
    if (
      Array.isArray(rebuyProduct.collection_ids) &&
      rebuyProduct.collection_ids.length > 0
    ) {
      collections = await fetchCollectionTitles(rebuyProduct);
    }

    const product = {
      id: rebuyProduct?.id,
      title: rebuyProduct?.title,
      handle: rebuyProduct?.handle,
      vendor: rebuyProduct?.vendor,
      tags: tags,
      price: parseFloat(rebuyProduct?.variants[0]?.price || 0) * 100,
      compare_at_price: rebuyProduct?.variants[0]?.compare_at_price
        ? parseFloat(rebuyProduct.variants[0].compare_at_price) * 100
        : null,
      // Map images to match Shopify format (just URLs, not objects)
      images: (rebuyProduct?.images || []).map(img => img.src),
      featured_image:
        rebuyProduct?.image?.src || rebuyProduct?.images?.[0]?.src,
      collections: collections,
      // Map options to match Shopify format (array of strings like ["Color", "Size"])
      options: (rebuyProduct?.options || []).map(opt => opt.name),
      variants: rebuyProduct?.variants.map(variant => ({
        id: variant.id,
        title: variant.title,
        available: variant.inventory_quantity > 0,
        price: parseFloat(variant.price) * 100,
        compare_at_price: variant.compare_at_price
          ? parseFloat(variant.compare_at_price) * 100
          : null,
        option1: variant.option1,
        option2: variant.option2,
        option3: variant.option3,
        // Add options array for each variant to match Shopify format
        options: [variant.option1, variant.option2, variant.option3].filter(
          opt => opt !== null && opt !== undefined
        ),
        featured_image: {
          src: variant.image_id
            ? rebuyProduct?.images.find(img => img.id === variant.image_id)?.src
            : null
        }
      })),
      url: `/products/${rebuyProduct?.handle}`,
      selected_or_first_available_variant: {
        url: `/products/${rebuyProduct?.handle}?variant=${rebuyProduct?.variants[0]?.id}`
      }
    };

    return product;
  }

  /**
   * Applies sale pricing logic to product data
   * @param {Object} product - Mapped product data
   * @returns {Object} Product data with sale pricing applied
   */
  function applySaleLogic(product) {
    // Get sale settings from global settings
    const saleSettings = getSaleSettings();

    if (!saleSettings.isEnabled) {
      return product;
    }

    // Check if product has the sale enabled tag
    const productTags = Array.isArray(product.tags) ? product.tags : [];
    const hasSaleTag = productTags.some(tag =>
      tag.toLowerCase().includes(saleSettings.saleTag.toLowerCase())
    );

    if (hasSaleTag) {
      const discountPercentage = saleSettings.discountPercentage / 100;
      const discountMultiplier = 1 - discountPercentage;

      product.sale_price = Math.round(product.price * discountMultiplier);

      if (!product.compare_at_price) {
        product.compare_at_price = product.price;
      }

      // Apply to all variants
      product.variants = product.variants.map(variant => ({
        ...variant,
        sale_price: Math.round(variant.price * discountMultiplier),
        compare_at_price: variant.compare_at_price || variant.price
      }));
    }

    return product;
  }

  /**
   * Gets sale settings from window variables
   * @returns {Object} Sale settings
   */
  function getSaleSettings() {
    return {
      isEnabled: window.isSaleEnabled || false,
      saleTag: window.saleEnabledTag || 'sitewide-eligible',
      discountPercentage: window.saleDiscountPercentage || 0
    };
  }

  /**
   * Fetches product recommendations from Rebuy
   * @param {string} productId - The Shopify product ID
   * @returns {Promise<Array>} Array of product recommendations
   */
  async function fetchRecommendations(productId) {
    try {
      const url = `${REBUY_BASE_URL}&shopify_product_ids=${productId}&format=pretty`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Rebuy API error: ${response.statusText}`);
      }

      const response_data = await response.json();

      // Rebuy API returns products in the 'data' property
      const products = response_data.data || [];

      if (!Array.isArray(products)) {
        return [];
      }

      if (products.length === 0) {
        console.log('No product recommendations returned');
        return [];
      }

      // Limit to first 4 products
      const limitedProducts = products.slice(0, 4);

      // Map and apply sale logic to products (now async)
      const mappedProductPromises = limitedProducts.map(async rebuyProduct => {
        const product = await mapRebuyProductToProduct(rebuyProduct);
        return product ? applySaleLogic(product) : null;
      });

      const mappedProducts = (await Promise.all(mappedProductPromises)).filter(
        product => product !== null
      );

      return mappedProducts;
    } catch (error) {
      console.error('Error fetching Rebuy recommendations:', error);
      return [];
    }
  }

  /**
   * Renders product recommendations into a container
   * @param {HTMLElement} container - Container element to render products into
   * @param {Array} products - Array of product data
   */
  function renderProducts(container, products) {
    if (!container || !products.length) return;

    // Clear existing content
    container.innerHTML = '';

    products.forEach((product, index) => {
      // Create product card element
      const productCard = document.createElement('product-card');

      // Get sale settings for rendering
      const saleSettings = getSaleSettings();
      const productTags = Array.isArray(product.tags) ? product.tags : [];
      const hasSaleTag = productTags.some(tag =>
        tag.toLowerCase().includes(saleSettings.saleTag.toLowerCase())
      );

      productCard.innerHTML = `
        <div class="product-card">
          <div class="product-card__tag"></div>
          <a
            href="${product.selected_or_first_available_variant.url}"
            class="product-card__image"
            tabindex="-1"
          >
            <img
              src="${product.featured_image}"
              alt="${product.title}"
              loading="lazy"
            />
            <div class="product-card__options" tabindex="-1"></div>
            <div class="product-card__cta" tabindex="-1"></div>
          </a>
          <a href="${
            product.url
          }" class="product-card__details" aria-label="Visit ${
        product.title
      } product page">
            <h3 class="product-card__title">${product.title}</h3>
            <p class="product-card__price">
              ${
                hasSaleTag && saleSettings.isEnabled && product.sale_price
                  ? `
                <span class="product-card__amount product-card__amount--compare">
                  $${(product.compare_at_price / 100).toFixed(0)}
                </span>
                <span class="product-card__amount red">$${(
                  product.sale_price / 100
                ).toFixed(2)}</span>
              `
                  : `
                ${
                  product.compare_at_price
                    ? `<span class="product-card__amount product-card__amount--compare">
                  $${(product.compare_at_price / 100).toFixed(2)}
                </span>`
                    : ''
                }
                <span class="product-card__amount">
                  $${(product.price / 100).toFixed(2)}
                </span>
              `
              }
            </p>
          </a>
          <div class="product-card__colors"></div>
        </div>
      `;

      const minimalProduct = {
        id: product.id,
        title: product.title,
        handle: product.handle,
        vendor: product.vendor,
        tags: product.tags,
        images: product.images,
        featured_image: product.featured_image,
        price: product.price,
        options: product.options,
        url: product.url,
        variants: product.variants.map(v => ({
          id: v.id,
          title: v.title,
          available: v.available,
          option1: v.option1,
          option2: v.option2,
          option3: v.option3,
          options: v.options,
          featured_image: v.featured_image
        })),
        rebuyProduct: true
      };

      productCard.setAttribute('product', JSON.stringify(minimalProduct));

      // Add collections if available
      if (product.collections && product.collections.length) {
        productCard.setAttribute(
          'collections',
          JSON.stringify(product.collections)
        );
      }

      container.appendChild(productCard);
    });

    // Render promo tags after all product cards are added to the DOM
    getPromoTags();
  }

  /**
   * Initializes product recommendations for a given section
   * @param {string} sectionId - The section ID to initialize
   * @param {Object} params - Parameters for the Rebuy API
   */
  async function init(productId) {
    const container = document.querySelector('.js-product-recommendations');
    if (!container) {
      console.warn(
        'Product recommendations container (.js-product-recommendations) not found in section'
      );
      return;
    }

    // Show loading state
    container.classList.add('loading');

    try {
      const products = await fetchRecommendations(productId);
      renderProducts(container, products);
    } catch (error) {
      console.error('Error initializing product recommendations:', error);
    } finally {
      container.classList.remove('loading');
    }
  }

  return {
    init,
    fetchRecommendations,
    renderProducts,
    mapRebuyProductToProduct,
    applySaleLogic
  };
})();
